import { useState } from 'react';

import Button from '../../../components/Button/Button';
import Card from '../../../components/Card/Card';
import DialogModal from '../../../components/DialogModal/DialogModal';
import InlineIcon from '../../../components/InlineIcon/InlineIcon';
import PaddedBox from '../../../components/PaddedBox/PaddedBox';
import Stack from '../../../components/Stack/Stack';
import LocaleCode from '../../types/LocaleCode';
import { useLocalization } from '../LocalizationProvider/LocalizationProvider';
import OverlayHeaderButton from '../OverlayHeaderButton/OverlayHeaderButton';

import globeIcon from './graphics/globe.svg?raw';

type ListOfLocalesProps = {
    locales: Record<string, LocaleCode>;
    onClick: (locale: LocaleCode) => void;
};

const ListOfLocales = ({ locales, onClick }: ListOfLocalesProps) => {
    return (
        <Stack space="small">
            {Object.values(locales).map((locale) => (
                <Button
                    fullWidth
                    key={locale.code}
                    variant="subtle"
                    size="large"
                    onClick={() => onClick(locale)}
                >
                    {locale.shortName}
                </Button>
            ))}
        </Stack>
    );
};

const LocaleSwitch = () => {
    const { switchLocale, locales, currentLocale } = useLocalization();

    const [isShowingLocaleSwitch, setIsShowingLocaleSwitch] = useState(false);
    const handleSwitch = (locale: LocaleCode) => {
        setIsShowingLocaleSwitch(false);
        switchLocale(locale);
    };

    if (Object.values(locales).length <= 1) {
        return null;
    }

    return (
        <>
            <OverlayHeaderButton
                icon={globeIcon}
                onClick={() => setIsShowingLocaleSwitch(!isShowingLocaleSwitch)}
            >
                {currentLocale?.shortName ?? '...'}
            </OverlayHeaderButton>

            <DialogModal
                isOpen={isShowingLocaleSwitch}
                onCloseRequested={() => setIsShowingLocaleSwitch(false)}
                width="small"
                unstyled
            >
                <Card horizontalPadding="small" verticalPadding="small">
                    <Stack>
                        <PaddedBox horizontal="center" verticalPadding="medium">
                            <InlineIcon
                                src={globeIcon}
                                size="xxlarge"
                                color="currentColor"
                            />
                        </PaddedBox>
                        <ListOfLocales
                            locales={locales}
                            onClick={handleSwitch}
                        />
                    </Stack>
                </Card>
            </DialogModal>
        </>
    );
};

export default LocaleSwitch;
